"use client"

import { useState, useContext } from "react"
import BuscadorItemsUbicaciones from "./BuscadorItemsUbicaciones.js"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  Alert,
  Fade,
  alpha,
  useTheme,
  Tooltip,
  Avatar,
  InputAdornment,
} from "@mui/material"

// Icons
import DeleteIcon from "@mui/icons-material/Delete"
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu"
import KitchenIcon from "@mui/icons-material/Kitchen"
import SaveIcon from "@mui/icons-material/Save"
import UpdateIcon from "@mui/icons-material/Update"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import InventoryIcon from "@mui/icons-material/Inventory"
import ScaleIcon from "@mui/icons-material/Scale"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import InfoIcon from "@mui/icons-material/Info"

const RecetaComponent = () => {
  const theme = useTheme()
  const { getItemAndItemUbicacion, getItemAtributoNoEliminados, crearRecetaApi, getBomByIdItem, updateRecetaApi } =
    useContext(ErpContext)

  const [baseItem, setBaseItem] = useState(null)
  const [ingredientes, setIngredientes] = useState([])
  const [cantidades, setCantidades] = useState({})
  const [searchFilters, setSearchFilters] = useState(null)
  const [recetaExistente, setRecetaExistente] = useState(false)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  const page = 1

  const handleFilterSearch = async ({ ubicacionId, itemId, precio, soloStockBajo, mostrarUltimosCreados }) => {
    if (!itemId) return
    try {
      setLoading(true)
      await getItemAtributoNoEliminados()
      const response = await getItemAndItemUbicacion(
        page,
        100,
        ubicacionId,
        itemId,
        precio,
        soloStockBajo,
        mostrarUltimosCreados,
      )

      const items = response?.data || []
      if (!baseItem && items.length > 0) {
        setBaseItem(items[0])
        const ingredientesAsociados = await getBomByIdItem(items[0].idItem)
        if (ingredientesAsociados.length > 0) {
          const nuevosIngredientes = ingredientesAsociados.map((bom) => ({
            ...bom.materiaPrima,
            idItem: bom.idMateriaPrima,
            cantidad: bom.cantidad,
          }))

          setIngredientes(nuevosIngredientes)

          const cantidadesMap = {}
          ingredientesAsociados.forEach((bom) => {
            cantidadesMap[`${bom.idMateriaPrima}-1`] = bom.cantidad.toString()
          })
          setCantidades(cantidadesMap)

          setRecetaExistente(true)
        }

        if (items.length > 1) {
          setIngredientes((prev) => [...prev, ...items.slice(1)])
        }
      } else {
        setIngredientes((prev) => [...prev, ...items])
      }
    } catch (error) {
      console.error("Error fetching inventory data:", error)
      setError("Error al buscar items")
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveIngredient = (key) => {
    setIngredientes((prev) => prev.filter((item) => `${item.idItem}-${item.idUbicacion || 1}` !== key))
    setCantidades((prev) => {
      const updated = { ...prev }
      delete updated[key]
      return updated
    })
  }

  const handleRemoveBaseItem = () => {
    setBaseItem(null)
    setIngredientes([])
    setCantidades({})
    setRecetaExistente(false)
  }

  const onSearch = (filters) => {
    setSearchFilters(filters)
    handleFilterSearch(filters)
  }

  const crearReceta = async () => {
    if (!baseItem) {
      setError("Debe seleccionar un item base para la receta")
      return
    }

    if (ingredientes.length === 0) {
      setError("Debe agregar al menos un ingrediente")
      return
    }

    try {
      setLoading(true)
      const ingredientesConCantidad = ingredientes.map((item) => {
        const key = `${item.idItem}-${item.idUbicacion || 1}`
        return {
          ...item,
          cantidad: cantidades[key] || "0",
        }
      })

      if (recetaExistente) {
        await crearRecetaApi(baseItem.idItem, ingredientesConCantidad, true)
      } else {
        await crearRecetaApi(baseItem.idItem, ingredientesConCantidad, false)
      }

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (error) {
      console.error("Error creando o actualizando receta:", error)
      setError("Error al crear/actualizar la receta")
    } finally {
      setLoading(false)
    }
  }

  const calcularTotalIngredientes = () => {
    return ingredientes.reduce((total, item) => {
      const key = `${item.idItem}-${item.idUbicacion || 1}`
      const cantidad = Number.parseFloat(cantidades[key] || 0)
      return total + cantidad
    }, 0)
  }

  const validarCantidades = () => {
    return ingredientes.every((item) => {
      const key = `${item.idItem}-${item.idUbicacion || 1}`
      const cantidad = Number.parseFloat(cantidades[key] || 0)
      return cantidad > 0
    })
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <Card elevation={3} sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <RestaurantMenuIcon
              sx={{
                fontSize: 32,
                color: theme.palette.primary.main,
                mr: 2,
              }}
            />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: "#2c3e50", mb: 0.5 }}>
                Gestión de Recetas 
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Crea y gestiona las listas de materiales para tus productos
              </Typography>
            </Box>
          </Box>

          {/* Estado de la receta */}
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Chip
              icon={baseItem ? <CheckCircleIcon /> : <InfoIcon />}
              label={baseItem ? "Item base seleccionado" : "Selecciona un item base"}
              color={baseItem ? "success" : "default"}
              variant={baseItem ? "filled" : "outlined"}
            />
            <Chip
              icon={<InventoryIcon />}
              label={`${ingredientes.length} ingredientes`}
              color={ingredientes.length > 0 ? "primary" : "default"}
              variant="outlined"
            />
            {recetaExistente && (
              <Chip icon={<UpdateIcon />} label="Receta existente" color="warning" variant="filled" />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Buscador */}
      <Box sx={{ mb: 3 }}>
        <BuscadorItemsUbicaciones onSearch={onSearch} />
      </Box>

      {/* Contenido principal */}
      <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", lg: "row" } }}>
        {/* Item Base */}
        <Card
          elevation={2}
          sx={{
            flex: { xs: 1, lg: 0.4 },
            borderRadius: 3,
            border: baseItem ? `2px solid ${theme.palette.success.main}` : `2px dashed ${theme.palette.grey[300]}`,
            backgroundColor: baseItem ? alpha(theme.palette.success.main, 0.02) : "transparent",
          }}
        >
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                <KitchenIcon />
              </Avatar>
            }
            title="Item Base de la Receta"
            subheader="El producto principal que se fabricará"
            sx={{ pb: 1 }}
          />
          <Divider />
          <CardContent sx={{ p: 2 }}>
            {baseItem ? (
              <Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.dark, mb: 0.5 }}>
                        {baseItem.descripcion}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Código: {baseItem.codigo}
                      </Typography>
                      {baseItem.codigoScanner && (
                        <Typography variant="body2" color="text.secondary">
                          Scanner: {baseItem.codigoScanner}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Remover item base">
                      <IconButton
                        size="small"
                        onClick={handleRemoveBaseItem}
                        sx={{
                          color: theme.palette.error.main,
                          backgroundColor: alpha(theme.palette.error.main, 0.1),
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.error.main, 0.2),
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Información adicional */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip size="small" label="Item Principal" color="success" variant="outlined" />
                  {recetaExistente && <Chip size="small" label="Con Receta" color="warning" variant="outlined" />}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: theme.palette.text.secondary,
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1" gutterBottom>
                  Busca un ítem para iniciar la receta
                </Typography>
                <Typography variant="body2">
                  El primer item que agregues se convertirá en la base de tu receta
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Ingredientes */}
        <Card elevation={2} sx={{ flex: { xs: 1, lg: 0.6 }, borderRadius: 3 }}>
          <CardHeader
            avatar={
              <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                <InventoryIcon />
              </Avatar>
            }
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Ingredientes y Materiales
                <Chip size="small" label={ingredientes.length} color="primary" />
              </Box>
            }
            subheader="Materiales necesarios para fabricar el producto"
            action={
              ingredientes.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ScaleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Total: {calcularTotalIngredientes().toFixed(2)}
                  </Typography>
                </Box>
              )
            }
            sx={{ pb: 1 }}
          />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            {ingredientes.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Cantidad</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Acción</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ingredientes.map((item) => {
                      const key = `${item.idItem}-${item.idUbicacion || 1}`
                      const cantidad = cantidades[key] || ""
                      const isValidQuantity = Number.parseFloat(cantidad) > 0

                      return (
                        <TableRow
                          key={key}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.02),
                            },
                            backgroundColor: !isValidQuantity ? alpha(theme.palette.warning.main, 0.05) : "inherit",
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.descripcion}
                              </Typography>
                              {item.codigoScanner && (
                                <Typography variant="caption" color="text.secondary">
                                  Scanner: {item.codigoScanner}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={item.codigo} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <TextField
                              variant="outlined"
                              size="small"
                              value={cantidad}
                              onChange={(e) => setCantidades((prev) => ({ ...prev, [key]: e.target.value }))}
                              placeholder="0,00"
                              type="number"
                              inputProps={{
                                inputMode: "decimal",
                                step: "0.01",
                                min: "0",
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <ScaleIcon fontSize="small" color="action" />
                                  </InputAdornment>
                                ),
                              }}
                              error={!isValidQuantity && cantidad !== ""}
                              sx={{
                                width: 120,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <Tooltip title="Eliminar ingrediente">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveIngredient(key)}
                                sx={{
                                  color: theme.palette.error.main,
                                  "&:hover": {
                                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  color: theme.palette.text.secondary,
                }}
              >
                <InventoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1" gutterBottom>
                  No hay ingredientes agregados
                </Typography>
                <Typography variant="body2">Usa el buscador para agregar materiales a tu receta</Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Botón de acción */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={crearReceta}
          disabled={loading || !baseItem || ingredientes.length === 0 || !validarCantidades()}
          startIcon={recetaExistente ? <UpdateIcon /> : <SaveIcon />}
          sx={{
            borderRadius: 3,
            textTransform: "none",
            boxShadow: 3,
            py: 1.5,
            px: 4,
            fontSize: "1rem",
            fontWeight: 600,
            backgroundColor: "#5b5b5f",
            "&:hover": {
              backgroundColor: alpha("#5b5b5f", 0.9),
              transform: "translateY(-2px)",
              boxShadow: 4,
            },
            "&:disabled": {
              backgroundColor: alpha("#5b5b5f", 0.6),
            },
            transition: "all 0.3s ease",
          }}
        >
          {loading ? "Procesando..." : recetaExistente ? "Actualizar Receta" : "Crear Receta"}
        </Button>
      </Box>

      {/* Validaciones y alertas */}
      {baseItem && ingredientes.length > 0 && !validarCantidades() && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              Algunos ingredientes no tienen cantidad válida. Asegúrate de que todas las cantidades sean mayores a 0.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Alertas de estado */}
      <Fade in={success}>
        <Alert
          severity="success"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 2,
            boxShadow: 3,
          }}
          onClose={() => setSuccess(false)}
        >
          {recetaExistente ? "Receta actualizada correctamente" : "Receta creada correctamente"}
        </Alert>
      </Fade>

      {error && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            borderRadius: 2,
            boxShadow: 3,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
    </Box>
  )
}

export default RecetaComponent
